import { forwardRef, type SelectHTMLAttributes } from 'react';
import { useDensityClass } from '@/design/density';
import { cx } from './cx';
import { useFieldControl } from './Field';
import { CONTROL_CLASS } from './Input';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options?: readonly SelectOption[];
  /**
   * Optgroups, for lists long enough that a flat scroll is a search.
   * Rendered after `options`, so a call site can put an "all" entry above the
   * groups without inventing a group to hold it.
   */
  groups?: readonly { label: string; options: readonly SelectOption[] }[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, groups, placeholder, className, ...rest },
  ref,
) {
  const field = useFieldControl();
  const size = useDensityClass({ public: 'px-3 py-2.5 text-sm', app: 'px-2.5 py-1.5 text-xs' });
  return (
    <select ref={ref} {...field} {...rest} className={cx(CONTROL_CLASS, size, 'pr-8', className)}>
      {placeholder && <option value="">{placeholder}</option>}
      {options?.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
      {groups?.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
});
